















































































































import { Component, Vue } from "vue-property-decorator";
import DefaultLayout from "@/components/layouts/DefaultLayout.vue";
import TDSButton from "@/components/common/TDSButton.vue";
import TDSModal from "@/components/partials/TDSModal/TDSModal.vue";
import TDSSelect from "@/components/common/TDSSelect.vue";
import Inputfield from "@/components/partials/Inputfield.vue";
import CountryPicker from "@/components/partials/CountryPicker.vue";
import TableList from "@/components/partials/TableList.vue";
import TDSSpinner from "@/components/common/TDSSpinner.vue";
import { InvitedUser } from "@/interfaces/entities/InvitedUser";

type RoleItem = {
    role: string;
    name: string;
};

enum ModalType {
    INVITE = "invite",
    UPDATE = "update"
}

@Component({
    components: { Inputfield, TableList, TDSSelect, TDSModal, TDSButton, TDSSpinner, DefaultLayout, CountryPicker }
})
export default class Members extends Vue {

    private isLoading: boolean = false;
    private isSending: boolean = false;
    private showModal: boolean = false;
    private modalType: ModalType = ModalType.INVITE;
    private showConfirmDelete: boolean = false;
    private showResendInvite: boolean = false;
    private showResetPassword: boolean = false;
    private userToDelete?: InvitedUser;
    private users: InvitedUser[] = [];
    private roles: RoleItem[] = [
        { role: "ADMIN",   name: this.$t("account.members.invite.roles.admin").toString() },
        { role: "SUPPORT", name: this.$t("account.members.invite.roles.support").toString() },
        { role: "BILLING", name: this.$t("account.members.invite.roles.billing").toString() },
        { role: "DEVELOPER", name: this.$t("account.members.invite.roles.developer").toString() },
        { role: "SALES", name: this.$t("account.members.invite.roles.sales").toString() }
    ];
    private selectedRole: RoleItem = this.roles[1];
    private firstName: string = "";
    private lastName: string = "";
    private email: string = "";
    private updateId: string = "";
    private updateEmail: string = "";
    
    private tableColumns: object = {
        name: { label: this.$t("name"), sortable: false },
        email: { label: this.$t("email"), sortable: false, width: "200px" },
        joined: { label: this.$t("account.members.table.joined"), width: "80px", sortable: false },
        role: { label: this.$t("account.members.table.role"), width: "80px", sortable: false },
        actions: { sortable: false, width: "80px" }
    };
    
    get isMobile(): boolean {
        return this.$store.state.isMobile;
    }

    private roleName(role: string): string {
        return this.roles.find((item: RoleItem) => item.role == role)?.name ?? role;
    }

    created() {
        document.title = process.env.VUE_APP_THEME_TITLE + " | " + this.$t("account.members.pageTitle");
        this.init();
    }

    async init() {
        this.isLoading = true;
        try {
            this.users = await this.$store.dispatch("FETCH_TEAM_MEMBERS");
        } catch (e) {
            this.$toastr.error("Failed loading user information");
        } finally {
            this.isLoading = false;
        }
    }

    selectRole(item: RoleItem) {
        console.log("selected role:", item);
        this.selectedRole = item;
    }

    private async saveModal() {
        if (this.modalType == ModalType.INVITE) {
            await this.sendInvite();
        } else if (this.modalType == ModalType.UPDATE) {
            await this.updateInvitation();
        }
    }

    private emailReadOnly() {
        const editable = this.modalType == ModalType.INVITE;
        return !(editable || this.showResendInvite);
    }

    private async confirmDeleteInvitation(user: InvitedUser) {
        this.showConfirmDelete = true;
        this.userToDelete = user;
    }

    private async deleteInvitation() {
        this.isSending = true;
        try {
            await this.$store.dispatch("REMOVE_TEAM_MEMBER", this.userToDelete?.id);
            this.$toastr.success("User removed successfully");
            this.init();
        } catch (e) {
            if (e.response && e.response.data && e.response.data.message) this.$toastr.error(e.response.data.detail);
            else this.$toastr.error("Failed updating invitation");
        } finally {
            this.isSending = false;
            this.showConfirmDelete = false;
            this.userToDelete = undefined;
        }
    }

    editInvitation(user: InvitedUser) {
        const role = this.roles.find((item: RoleItem) => item.role == user.role);
        if (role == undefined) {
            // show error
            this.$toastr.error("User role not found");
            return;
        }
        this.showResendInvite = (user.status == "PENDING");
        this.showResetPassword = (user.status == "ACCEPTED");
        this.modalType = ModalType.UPDATE;
        this.updateId = user.id;
        this.updateEmail = user.email;
        this.firstName = user.firstName;
        this.lastName = user.lastName;
        this.email = user.email;
        this.selectedRole = role;
        this.showModal = true;
    }
        
    private async updateInvitation() {
        this.isSending = true;
        try {
            await this.$store.dispatch("UPDATE_TEAM_MEMBER", {
                id: this.updateId,
                role: this.selectedRole.role,
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email
            }); 
            this.showModal = false;
            this.$toastr.success("User updated successfully");
            this.firstName = "";
            this.lastName = "";
            this.email = "";
            this.updateEmail = "";
            this.init();
        } catch (e) {
            if (e.response && e.response.data && e.response.data.message) this.$toastr.error(e.response.data.detail);
            else this.$toastr.error("Failed updating invitation");
        } finally {
            this.isSending = false;
        }
    }

    private async sendInvite() {
        this.isSending = true;
        try {
            await this.$store.dispatch("INVITE_TEAM_MEMBER", {
                email: this.email,
                firstName: this.firstName,
                lastName: this.lastName,
                role: this.selectedRole.role
            }); 
            this.showModal = false;
            this.$toastr.success("Invitation sent successfully");
            this.firstName = "";
            this.lastName = "";
            this.email = "";
            this.updateEmail = "";
            this.init();
        } catch (e) {
            if (e.response && e.response.data && e.response.data.message) this.$toastr.error(e.response.data.detail);
            else this.$toastr.error("Failed updating personal information");
        } finally {
            this.isSending = false;
        }
    }

    showInviteModal() {
        this.modalType = ModalType.INVITE;
        this.showResendInvite = false;
        this.firstName = "";
        this.lastName = "";
        this.email = "";
        this.selectedRole = this.roles[1];
        this.showResendInvite = false;
        this.showResetPassword = false;
        this.showModal = true;
    }

    resendInvitation() {
        if (this.updateEmail != this.email) {
            this.$toastr.error("Please save your changes before resending the invitation");
            return;
        }
        this.$store.dispatch("RESEND_INVITATION", this.updateId).then(() => {
            this.$toastr.success("Invitation resent successfully");
            this.showModal = false;
        }).catch((e: any) => {
            if (e.response && e.response.data && e.response.data.message) this.$toastr.error(e.response.data.detail);
            else this.$toastr.error("Failed resending invitation");
        }).finally(() => {
            this.isSending = false;
        });
    }
    
    resetPassword() {
        if (this.updateEmail != this.email) {
            this.$toastr.error("Please save your changes before sending a password reset");
            return;
        }
        this.$store.dispatch("RESET_PASSWORD", this.updateId).then(() => {
            this.$toastr.success("Password reset email sent to user");
            this.showModal = false;
        }).catch((e: any) => {
            if (e.response && e.response.data && e.response.data.message) this.$toastr.error(e.response.data.detail);
            else this.$toastr.error("Failed to reset password");
        }).finally(() => {
            this.isSending = false;
        });
    }
}
